import { useState } from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

import { shorterFyID } from '@/utils/format';
import { zoomifyBaseURL } from '@/config';

import {
  ItemCatalogueEditModal,
  ItemPropertiesAddModal,
  ItemPropertiesEditModal,
  ItemPropertiesModal,
} from '../Modals';
import { Item, Property, Company, ITEMCLASSTYPE } from '../../types';

type ItemCatalogueTableProps = {
  data: (Item | undefined)[] | undefined;
  properties?: Property[];
  activeCompanies: Company[];
};

/**
 * item catalogue table simplified
 * @date 2025/03/21
 * @returns
 */
const ItemCatalogueTable = ({
  data: items,
  properties: allProperties,
  activeCompanies,
}: ItemCatalogueTableProps) => {
  const [activeModal, setActiveModal] = useState('');
  const [selectedItem, setSelectedItem] = useState<Item | undefined>();
  const [selectedItemProperty, setSelectedItemProperty] =
    useState<Item['properties'][0]>();

  const handleClose = () => {
    setSelectedItem(undefined);
    setActiveModal('');
  };

  // == show loading test while fetching items ==
  // @2025/12/17
  if (!items || !allProperties || items.length == 0) {
    return <div>Loading items ...</div>;
  }

  const company4Item = (item: Item | undefined) => {
    if (!item) return [];
    return item.companies.map((c) => (c as any).company_id);
  };

  return (
    <>
      {/* === Items table === */}
      <table className="table table-hover equal-width-columns border">
        <thead>
          <tr>
            <th>Index</th>
            <th>Icon</th>
            <th>Item ID</th>
            <th>Description</th>
            <th>Type</th>
            <th>Category</th>
            <th>Visible</th>
            <th>Classification</th>
            {/* @deprecated - 2025/03/20 */}
            {/* {allProperties?.map((property) => (
              <th key={property?.name}>{property?.label}</th>
            ))} */}
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr
              style={{ cursor: 'pointer' }}
              onClick={() => {
                setSelectedItem(item);
                setActiveModal('item-edit');
              }}
              key={item?.id}
            >
              <td>{i + 1}</td>
              <td>
                {item?.s3_icon_path && (
                  <img
                    src={zoomifyBaseURL + item?.s3_icon_path}
                    className="w-4 h-4"
                  />
                )}
              </td>
              <td data-tooltip={item?.id}>{shorterFyID(item?.id)}</td>
              <td data-tooltip={item?.description}>
                {shorterFyID(item?.description)}
              </td>
              <td>{item?.type}</td>
              <td>{item?.category}</td>
              <td data-field={item?.ui_visible}>
                {item?.ui_visible ? (
                  <span>
                    <EyeIcon className="w-4 h-4" />
                  </span>
                ) : (
                  <span className="text-muted">
                    <EyeSlashIcon className="w-4 h-4" />
                  </span>
                )}
              </td>
              <td data-companies={company4Item(item)}>
                {item?.classification_type == ITEMCLASSTYPE.C ? (
                  <span className="text-primary">{ITEMCLASSTYPE.C}</span>
                ) : (
                  ITEMCLASSTYPE.S
                )}
              </td>
              {/* @deprecated - 2025/03/20 */}
              {/* {allProperties?.map((property) => {
                  const matchingProperty = item?.properties.find(
                    (itemProperty) => itemProperty.name === property?.name
                  );
  
                  const formattedValue = matchingProperty
                    ? `${matchingProperty.metric_lower_bound}-${matchingProperty.metric_upper_bound}`
                    : '_';
                  return <td key={property?.name}>{formattedValue}</td>;
                })} */}
            </tr>
          ))}
        </tbody>
      </table>
      {/* == Modals section == */}
      {selectedItem && (
        <ItemCatalogueEditModal
          show={activeModal === 'item-edit'}
          selectedItem={selectedItem}
          activeCompanies={activeCompanies}
          handleClose={handleClose}
          showItemProperties={() => setActiveModal('item-properties')}
        />
      )}
      {
        <ItemPropertiesEditModal
          show={activeModal === 'item-properties-edit'}
          handleClose={handleClose}
          handleBack={() => setActiveModal('item-properties')}
          itemId={selectedItem?.id}
          selectedItemProperty={selectedItemProperty}
        />
      }
      {
        <ItemPropertiesAddModal
          show={activeModal === 'item-properties-add'}
          handleClose={handleClose}
          handleBack={() => setActiveModal('item-properties')}
          itemId={selectedItem?.id}
          properties={allProperties.filter(
            (p) =>
              !selectedItem?.properties
                .map((prop) => prop.name)
                .includes(p.name)
          )}
        />
      }
      {
        <ItemPropertiesModal
          show={activeModal === 'item-properties'}
          handleClose={handleClose}
          handleBack={() => setActiveModal('item-edit')}
          properties={selectedItem?.properties}
          selectItemProperty={(itemProperty: Item['properties'][0]) => {
            setSelectedItemProperty(itemProperty);
            setActiveModal('item-properties-edit');
          }}
          showItemPropertiesAddModal={() =>
            setActiveModal('item-properties-add')
          }
        />
      }
    </>
  );
};

export default ItemCatalogueTable;
